// CustomPrompts.jsx - Mobile-Optimized Version
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { 
  PlusCircle, 
  Edit, 
  Trash, 
  Save, 
  X, 
  BookOpen, 
  Sparkles, 
  Zap, 
  Star,
  LucideFilter, 
  Check,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUsageLimiter } from '../../utils/usageLimiter';
import { starCoinsClient } from '../../services/starCoinsClient';

// Sport options for filtering prompts
const SPORT_OPTIONS = [
  { value: 'all', label: 'All Sports' },
  { value: 'nfl', label: 'NFL' },
  { value: 'nba', label: 'NBA' },
  { value: 'mlb', label: 'MLB' },
  { value: 'nhl', label: 'NHL' },
  { value: 'epl', label: 'Premier League' },
  { value: 'ncaab', label: 'NCAAB' },
  { value: 'ncaaf', label: 'NCAAF' },
  { value: 'mls', label: 'MLS' }
];

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Utility functions for "don't show again" feature
const DONT_SHOW_STORAGE_KEY = 'pickstar_prompt_confirmations_hidden';
const DONT_SHOW_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const shouldShowConfirmation = () => {
  try {
    const stored = localStorage.getItem(DONT_SHOW_STORAGE_KEY);
    if (!stored) {
      console.log("No stored confirmation preference found");
      return true;
    }
    
    // Parse the stored data
    const data = JSON.parse(stored);
    if (!data || typeof data.timestamp !== 'number') {
      console.log("Invalid stored confirmation data, showing confirmation");
      return true;
    }
    
    const now = Date.now();
    const elapsed = now - data.timestamp;
    const shouldShow = elapsed > DONT_SHOW_DURATION;
    
    console.log("Confirmation preference check:", {
      storedTime: new Date(data.timestamp).toLocaleString(),
      currentTime: new Date(now).toLocaleString(),
      elapsedHours: Math.round(elapsed / (60 * 60 * 1000) * 10) / 10,
      shouldShow
    });
    
    return shouldShow;
  } catch (error) {
    console.error("Error checking confirmation preference:", error);
    return true; // Default to showing on error
  }
};

const hideFutureConfirmations = () => {
  try {
    const data = {
      timestamp: Date.now()
    };
    
    localStorage.setItem(DONT_SHOW_STORAGE_KEY, JSON.stringify(data));
    console.log("Saved confirmation preference:", data);
    return true;
  } catch (error) {
    console.error("Could not save confirmation preference:", error);
    return false;
  }
};

// Custom prompt service - handles data operations
export const promptService = {
  // Get user's prompts
  async getUserPrompts(userId, sportFilter = 'all') {
    try {
      const userPromptsRef = collection(db, 'users', userId, 'prompts');
      let q;
      
      if (sportFilter === 'all') {
        q = query(userPromptsRef, orderBy('createdAt', 'desc'));
      } else {
        q = query(userPromptsRef, where('sport', '==', sportFilter), orderBy('createdAt', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching user prompts:', error);
      return [];
    }
  },
  
  // Create a new prompt
  async createPrompt(userId, promptData) {
    try {
      // Add metadata
      const data = {
        ...promptData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: 0
      };
      
      const promptsRef = collection(db, 'users', userId, 'prompts');
      const docRef = await addDoc(promptsRef, data);
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error('Error creating prompt:', error);
      throw error;
    }
  },
  
  // Update an existing prompt
  async updatePrompt(userId, promptId, updates) {
    try {
      const promptRef = doc(db, 'users', userId, 'prompts', promptId);
      await updateDoc(promptRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating prompt:', error);
      throw error;
    }
  },
  
  // Delete a prompt
  async deletePrompt(userId, promptId) {
    try {
      const promptRef = doc(db, 'users', userId, 'prompts', promptId);
      await deleteDoc(promptRef);
      return true;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  }
};

// Mobile-Optimized Prompt Chip Component
export const PromptChip = ({ prompt, onClick, onEdit, onDelete, canUse = true, isMobile = false }) => {
  // Get the sport label if applicable
  const sportLabel = prompt.sport !== 'all' 
    ? SPORT_OPTIONS.find(opt => opt.value === prompt.sport)?.label 
    : null;
    
  return (
    <div className={`group relative bg-slate-800/40 hover:bg-slate-800/70 border border-slate-700/50 hover:border-emerald-500/30 rounded-lg transition-all duration-200 ${
      isMobile ? 'p-4 mobile-prompt-chip' : 'p-3'
    }`}>
      <div className="flex items-start">
        <div className={`${isMobile ? 'h-8 w-8' : 'h-7 w-7'} rounded-md flex items-center justify-center mr-2 ${
          sportLabel 
            ? 'bg-emerald-500/20 text-emerald-400' 
            : 'bg-slate-700/50 text-slate-300'
        }`}>
          <Sparkles className={`${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`${isMobile ? 'text-base' : 'text-sm'} font-medium text-white truncate`}>
            {prompt.name}
          </h4>
          {sportLabel && (
            <span className={`${isMobile ? 'text-sm' : 'text-xs'} text-emerald-400/80 block mt-0.5`}>
              {sportLabel}
            </span>
          )}
          {prompt.description && (
            <p className={`${isMobile ? 'text-sm' : 'text-xs'} text-slate-400 mt-1 line-clamp-2`}>
              {prompt.description}
            </p>
          )}
        </div>
        
        <div className={`ml-2 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-200 flex space-x-1`}>
          <button
            onClick={() => onEdit(prompt)}
            className={`${isMobile ? 'p-2.5' : 'p-1.5'} rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors`}
            title="Edit prompt"
          >
            <Edit className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
          </button>
          <button
            onClick={() => onDelete(prompt)}
            className={`${isMobile ? 'p-2.5' : 'p-1.5'} rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors`}
            title="Delete prompt"
          >
            <Trash className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
          </button>
        </div>
      </div>
      
      <button
        onClick={() => canUse && onClick(prompt)}
        disabled={!canUse}
        className={`w-full ${isMobile ? 'mt-4 px-4 py-3 text-sm' : 'mt-3 px-3 py-1.5 text-xs'} font-medium flex items-center justify-center gap-1.5 rounded-md transition-colors ${
          canUse
            ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400"
            : "bg-slate-700/30 text-slate-500 cursor-not-allowed"
        }`}
      >
        {canUse ? <Sparkles className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} /> : <Lock className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />}
        <span>Run Prompt</span>
      </button>
    </div>
  );
};

// Mobile-Optimized Prompt Editor Modal Component
export const PromptEditorModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialPrompt = null,
  maxPrompts,
  remainingPrompts,
  canCreate,
  isMobile = false
}) => {
  const [name, setName] = useState(initialPrompt?.name || '');
  const [description, setDescription] = useState(initialPrompt?.description || '');
  const [promptText, setPromptText] = useState(initialPrompt?.promptText || '');
  const [sport, setSport] = useState(initialPrompt?.sport || 'all');
  const [errors, setErrors] = useState({});
  
  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setName(initialPrompt?.name || '');
      setDescription(initialPrompt?.description || '');
      setPromptText(initialPrompt?.promptText || '');
      setSport(initialPrompt?.sport || 'all');
      setErrors({});
    }
  }, [isOpen, initialPrompt]);
  
  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!promptText.trim()) newErrors.promptText = 'Prompt text is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Save prompt
  const handleSave = () => {
    if (!validate()) return;
    
    const promptData = {
      name,
      description,
      promptText,
      sport
    };
    
    onSave(promptData, initialPrompt?.id);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div
        className={`bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full ${
          isMobile 
            ? 'max-w-full max-h-[95vh] mx-2 mobile-editor-modal' 
            : 'max-w-lg max-h-[90vh]' 
        } overflow-y-auto my-auto relative`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between mb-4 ${isMobile ? 'mobile-modal-header' : ''}`}>
          <div className="flex items-center gap-2">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} bg-gradient-to-br from-emerald-500/20 to-violet-500/20 rounded-lg flex items-center justify-center`}>
              {initialPrompt ? (
                <Edit className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-emerald-400`} />
              ) : (
                <PlusCircle className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-emerald-400`} />
              )}
            </div>
            <h3 className={`${isMobile ? 'text-xl' : 'text-lg'} font-medium text-white`}>
              {initialPrompt ? 'Edit Prompt' : 'Create New Prompt'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`${isMobile ? 'p-2.5' : 'p-1.5'} text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors`}
          >
            <X className={`${isMobile ? 'w-6 h-6' : 'w-5 h-5'}`} />
          </button>
        </div>
        
        {/* Warning if at limit */}
        {!initialPrompt && !canCreate && (
        <div className={`mb-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg ${isMobile ? 'mobile-warning' : ''}`}>
          <div className="flex gap-2">
            <Star className={`${isMobile ? 'w-6 h-6' : 'w-5 h-5'} text-violet-400 flex-shrink-0`} />
            <div>
              <p className={`text-violet-300 font-medium ${isMobile ? 'text-base' : ''}`}>Free prompts used</p>
              <p className={`text-violet-200/70 ${isMobile ? 'text-sm' : 'text-sm'} mt-0.5`}>
                You've used your {maxPrompts} free custom prompts. 
                Create additional prompts for 33 Star Coins each.
              </p>
            </div>
          </div>
        </div>
      )}
        
        {/* Form */}
        <div className={`space-y-4 ${isMobile ? 'mobile-form' : ''}`}>
          {/* Name field */}
          <div>
            <label htmlFor="prompt-name" className={`block text-white ${isMobile ? 'text-base' : 'text-sm'} font-medium mb-2`}>
              Prompt Name
            </label>
            <input
              id="prompt-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="E.g., Underdog Analysis, Value Bet Finder"
              className={`w-full bg-slate-900/50 border ${
                errors.name ? 'border-red-500/50' : 'border-slate-700/50'
              } rounded-lg ${isMobile ? 'p-4 text-base' : 'p-2.5 text-sm'} text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50`}
            />
            {errors.name && (
              <p className={`mt-1 ${isMobile ? 'text-sm' : 'text-xs'} text-red-400`}>{errors.name}</p>
            )}
          </div>
          
          {/* Description field */}
          <div>
            <label htmlFor="prompt-desc" className={`block text-white ${isMobile ? 'text-base' : 'text-sm'} font-medium mb-2`}>
              Description (Optional)
            </label>
            <input
              id="prompt-desc"
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What this prompt is useful for"
              className={`w-full bg-slate-900/50 border border-slate-700/50 rounded-lg ${isMobile ? 'p-4 text-base' : 'p-2.5 text-sm'} text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50`}
            />
          </div>
          
          {/* Sport selection */}
          <div>
            <label htmlFor="prompt-sport" className={`block text-white ${isMobile ? 'text-base' : 'text-sm'} font-medium mb-2`}>
              Sport (Optional)
            </label>
            <select
              id="prompt-sport"
              value={sport}
              onChange={e => setSport(e.target.value)}
              className={`w-full bg-slate-900/50 border border-slate-700/50 rounded-lg ${isMobile ? 'p-4 text-base' : 'p-2.5 text-sm'} text-white focus:outline-none focus:ring-1 focus:ring-emerald-500/50`}
            >
              {SPORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className={`mt-1 ${isMobile ? 'text-sm' : 'text-xs'} text-slate-400`}>
              Associate this prompt with a specific sport for easier filtering
            </p>
          </div>
          
          {/* Prompt text */}
          <div>
            <label htmlFor="prompt-text" className={`block text-white ${isMobile ? 'text-base' : 'text-sm'} font-medium mb-2`}>
              Prompt Instructions
            </label>
            <textarea
              id="prompt-text"
              value={promptText}
              onChange={e => setPromptText(e.target.value)}
              placeholder="Enter your custom instructions for the AI..."
              rows={isMobile ? 8 : 6}
              className={`w-full bg-slate-900/50 border ${
                errors.promptText ? 'border-red-500/50' : 'border-slate-700/50'
              } rounded-lg ${isMobile ? 'p-4 text-base' : 'p-2.5 text-sm'} text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 resize-none`}
            ></textarea>
            {errors.promptText ? (
              <p className={`mt-1 ${isMobile ? 'text-sm' : 'text-xs'} text-red-400`}>{errors.promptText}</p>
            ) : (
              <p className={`mt-1 ${isMobile ? 'text-sm' : 'text-xs'} text-slate-400`}>
                Write detailed instructions for how the AI should analyze games or provide insights
              </p>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'justify-end'} gap-3 mt-6`}>
          <button
            onClick={onClose}
            className={`${isMobile ? 'w-full py-3' : ''} px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            // Remove the disabled condition completely
            className={`${isMobile ? 'w-full py-3' : ''} px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white`}
          >
            <Save className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
            <span>
              {!initialPrompt && !canCreate 
                ? `Save Prompt (33 Star Coins)` 
                : 'Save Prompt'
              }
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Mobile-Optimized Delete confirmation modal
export const DeletePromptModal = ({ isOpen, onClose, onConfirm, promptName, isMobile = false }) => {
  console.log('DeletePromptModal render:', { isOpen, promptName });
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div 
        className={`bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-6 w-full ${
          isMobile ? 'max-w-sm mx-4' : 'max-w-md'
        } relative`}
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <div className={`mx-auto ${isMobile ? 'w-16 h-16' : 'w-12 h-12'} rounded-full bg-red-500/20 flex items-center justify-center mb-4`}>
            <Trash className={`${isMobile ? 'w-8 h-8' : 'w-6 h-6'} text-red-400`} />
          </div>
          <h3 className={`${isMobile ? 'text-xl' : 'text-xl'} font-medium text-white mb-3`}>Delete Prompt</h3>
          <p className={`text-white/70 ${isMobile ? 'text-base' : ''}`}>
            Are you sure you want to delete <strong className="text-red-300">"{promptName}"</strong>?
            <br />This action cannot be undone.
          </p>
        </div>
        
        <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'space-x-3'}`}>
          <button
            onClick={() => {
              console.log('Cancel clicked');
              onClose();
            }}
            className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2.5'} px-4 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              console.log('Delete confirmed for:', promptName);
              onConfirm();
            }}
            className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2.5'} px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium`}
          >
            Delete Prompt
          </button>
        </div>
        
        {/* Close button */}
        <button
          onClick={() => {
            console.log('X button clicked');
            onClose();
          }}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Mobile-Optimized Usage Limit Modal
// CRITICAL UPDATE (2025-09-29): Added resetTime prop to show when limits reset
export const UsageLimitModal = ({ isOpen, onClose, onUpgrade, limit, tier, resetTime, isMobile = false }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            className={`bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full ${
              isMobile ? 'max-w-sm mx-4' : 'max-w-md'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className={`mx-auto ${isMobile ? 'w-16 h-16' : 'w-12 h-12'} rounded-full bg-amber-500/20 flex items-center justify-center mb-3`}>
                <AlertTriangle className={`${isMobile ? 'w-8 h-8' : 'w-6 h-6'} text-amber-400`} />
              </div>
              <h3 className={`${isMobile ? 'text-xl' : 'text-xl'} font-medium text-white mb-2`}>Usage Limit Reached</h3>
              <p className={`text-white/70 ${isMobile ? 'text-base' : ''}`}>
                {tier === 'basic'
                  ? `You've reached your daily limit of ${limit} custom prompt use.`
                  : `You've reached your daily limit of ${limit} custom prompt uses on your Pro plan.`}
              </p>
              {/* CRITICAL UPDATE (2025-09-29): Added reset time info to limit modal */}
              {resetTime && (
                <p className={`text-amber-300 ${isMobile ? 'text-sm' : 'text-sm'} mt-2`}>
                  Your usage will reset at midnight (local time)
                </p>
              )}
            </div>
            
            <div className={`bg-slate-900/50 rounded-lg p-4 my-4 border border-slate-700/50 ${isMobile ? 'mobile-plan-info' : ''}`}>
              <div className={`flex items-center justify-between mb-2 ${isMobile ? 'text-base' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-500/20 rounded-md">
                    <Zap className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-emerald-400`} />
                  </div>
                  <span className="text-white font-medium">Basic Plan</span>
                </div>
                <span className="text-white/60">1 use/day</span>
              </div>
              
              <div className={`flex items-center justify-between ${isMobile ? 'text-base' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-violet-500/20 rounded-md">
                    <Star className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-violet-400`} />
                  </div>
                  <span className="text-white font-medium">Pro Plan</span>
                </div>
                <span className="text-white/60">7 uses/day</span>
              </div>
            </div>
            
            <div className={`mt-3 text-center ${isMobile ? 'mobile-star-coins-info' : ''}`}>
              <p className={`text-white/70 ${isMobile ? 'text-base' : 'text-sm'}`}>
                <Star className={`inline-block ${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} mr-1 text-amber-400`} />
                You can also use 11 Star Coins per prompt when you've reached your daily limit
              </p>
            </div>
            
            <div className={`flex ${isMobile ? 'flex-col space-y-3' : ''} gap-3 mt-6`}>
              <button
                onClick={onClose}
                className={`${isMobile ? 'w-full py-3' : 'flex-1'} px-4 py-2.5 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors`}
              >
                Close
              </button>
              {tier === 'basic' && (
                <button
                  onClick={onUpgrade}
                  className={`${isMobile ? 'w-full py-3' : 'flex-1'} px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-lg transition-colors`}
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Updated Star Coins confirmation modal with mobile optimization
export const StarCoinsConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  cost, 
  promptName, 
  action,
  insufficient = false, // Add this prop
  balance = 0, // Add this prop
  showDontShowAgain = true,
  isMobile = false 
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  // Add debugging for modal state
  useEffect(() => {
    console.log('StarCoinsConfirmModal state:', { isOpen, promptName, action, cost });
  }, [isOpen, promptName, action, cost]);

  const handleConfirm = () => {
    try {
      // Apply "don't show again" preference first
      if (dontShowAgain) {
        hideFutureConfirmations();
      }
      
      // Call the confirm callback
      if (typeof onConfirm === 'function') {
        onConfirm();
      } else {
        console.error('StarCoinsConfirmModal: onConfirm is not a function');
      }
    } catch (error) {
      console.error('Error in StarCoinsConfirmModal confirm:', error);
    }
  };

  if (insufficient) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              className={`bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full ${
                isMobile ? 'max-w-sm mx-4' : 'max-w-sm'
              }`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`flex items-center gap-3 mb-4`}>
                <AlertTriangle className={`w-6 h-6 text-amber-400`} />
                <h3 className="text-lg font-medium text-white">
                  Insufficient Star Coins
                </h3>
              </div>
              
              <p className="text-white/80 mb-4">
                You need {cost} Star Coins to {action} "{promptName}".
                <br />
                Current balance: {balance} Star Coins
              </p>
              
              <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'space-x-3'}`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2'} border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg transition-colors`}
                >
                  Close
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirm();
                  }}
                  className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2'} bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white rounded-lg transition-colors`}
                >
                  Get Star Coins
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 400 }}
            className={`bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full ${
              isMobile ? 'max-w-sm mx-4' : 'max-w-sm'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-center gap-3 mb-4 ${isMobile ? 'mobile-star-coins-header' : ''}`}>
              <Star className={`${isMobile ? 'w-8 h-8' : 'w-6 h-6'} text-amber-400`} />
              <h3 className={`${isMobile ? 'text-lg' : 'text-lg'} font-medium text-white`}>
                {action === 'create' ? 'Create' : 'Run'} {promptName}?
              </h3>
            </div>
            
            <p className={`text-white/80 mb-4 ${isMobile ? 'text-base' : ''}`}>
              This will use {cost} Star Coins.
            </p>

            {showDontShowAgain && (
              <div className={`flex items-center gap-2 mb-4 p-3 bg-slate-900/50 rounded-lg ${isMobile ? 'mobile-checkbox-row' : ''}`}>
                <input
                  type="checkbox"
                  id="dont-show-again"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-amber-500 bg-slate-700 border-slate-600 rounded focus:ring-amber-500 focus:ring-2`}
                />
                <label htmlFor="dont-show-again" className={`${isMobile ? 'text-sm' : 'text-sm'} text-white/70`}>
                  Don't show confirmations for 24 hours
                </label>
              </div>
            )}
            
            <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'space-x-3'}`}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2'} border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirm();
                }}
                className={`${isMobile ? 'w-full py-3' : 'flex-1 py-2'} bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white rounded-lg transition-colors`}
              >
                Confirm
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main component for Custom Prompts feature - Mobile Optimized
const CustomPrompts = ({ 
  userId,
  gameInfo = null,
  onUsePrompt,
  onUpgrade,
  isMobile: propIsMobile = false,
  showInSuggestions = true,
  onModalStateChange = null,
  usePortalsForModals = false
}) => {
  const detectedIsMobile = useIsMobile();
  const isMobile = propIsMobile || detectedIsMobile;

  // Normalize userId - ensuring it's properly formatted for Firebase
  const normalizedUserId = useMemo(() => {
    if (typeof userId === 'string' && userId) {
      return userId;
    } else if (userId && typeof userId === 'object') {
      return userId.uid || userId.id || null;
    }
    return null;
  }, [userId]);
  
  // State
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [sportFilter, setSportFilter] = useState('all');
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  
  // Use the existing hook from useUsageLimiter instead of creating a new one
  const usageLimiter = useUsageLimiter(normalizedUserId);

  const [showStarCoinsConfirm, setShowStarCoinsConfirm] = useState({
    visible: false,
    cost: 0,
    promptId: null,
    promptName: '',
    action: null,
    pendingPromptData: null,
    insufficient: false, // Add this flag
    balance: 0  // ✅ Add this
  });
  
  // Extract relevant prompt limit data from usageLimiter
  // CRITICAL UPDATE (2025-09-29): Added promptResetTime and currentPromptUsage
  // for displaying countdown timer and current usage status
  const {
    canCreatePrompt,
    remainingPrompts,
    maxPrompts,
    canUsePrompt,
    starCoins,
    remainingUsage,
    maxUsage,
    tier,
    promptResetTime,       // Added: Next midnight for countdown timer
    currentPromptUsage,     // Added: Current usage count for display
    checkPromptCreateLimit,
    checkPromptUsageLimit,
    incrementPromptUsage
  } = usageLimiter;
  
  // Load prompts
  const loadPrompts = useCallback(async () => {
    if (!normalizedUserId) {
      console.warn('Cannot load prompts: No valid userId');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const userPrompts = await promptService.getUserPrompts(normalizedUserId, sportFilter);
      setPrompts(userPrompts);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  }, [normalizedUserId, sportFilter]);
  
  // Load prompts on mount and when filter changes
  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);
  
  // Debug state changes
  useEffect(() => {
    console.log('Modal state changed:', { deleteModalOpen, promptToDelete: promptToDelete?.name });
  }, [deleteModalOpen, promptToDelete]);
  
  // Initialize once on component mount
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && normalizedUserId) {
      hasInitializedRef.current = true;
      
      // Initial check of limits
      checkPromptCreateLimit();
      checkPromptUsageLimit();
    }
  }, [normalizedUserId, checkPromptCreateLimit, checkPromptUsageLimit]);
  
    // 🆕 ADD: Effect to notify parent when modals open/close
    useEffect(() => {
      if (onModalStateChange) {
        const isAnyModalOpen = editorOpen || deleteModalOpen || limitModalOpen || showStarCoinsConfirm.visible;
        onModalStateChange(isAnyModalOpen);
      }
    }, [editorOpen, deleteModalOpen, limitModalOpen, showStarCoinsConfirm.visible, onModalStateChange]); 

    const renderModal = (modalComponent) => {
      if (usePortalsForModals) {
        return createPortal(modalComponent, document.body);
      }
      return modalComponent;
    };

  // Create or update prompt
  // Create or update prompt - FIXED VERSION
const handleSavePrompt = async (promptData, promptId = null) => {
  if (!normalizedUserId) return;
  
  try {
    if (promptId) {
      // Update existing - no coins needed
      await promptService.updatePrompt(normalizedUserId, promptId, promptData);
      loadPrompts();
      setEditorOpen(false);
      return;
    }
    
    // For NEW prompts - CHECK FREE PROMPTS FIRST
    if (canCreatePrompt) {
      // User has remaining free prompts - create for free
      console.log('Creating prompt for free - user has remaining prompts:', remainingPrompts);
      await promptService.createPrompt(normalizedUserId, promptData);
      await loadPrompts();
      await checkPromptCreateLimit(); // Refresh limits
      setEditorOpen(false);
      return;
    }
    
    // User has used up their free prompts - offer Star Coins
    console.log('Free prompts exhausted, offering Star Coins option');
    
    if (shouldShowConfirmation()) {
      // Check if user has enough Star Coins
      const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptCreation');
      
      if (starCoinsStatus.canUse) {
        // Show Star Coins confirmation for creation
        setShowStarCoinsConfirm({
          visible: true,
          cost: starCoinsStatus.cost,
          promptId: null,
          promptName: promptData.name,
          action: 'create',
          pendingPromptData: promptData,
          insufficient: false,
          balance: 0
        });
      } else {
        // Can't use Star Coins - show insufficient coins message
        setShowStarCoinsConfirm({
          visible: true,
          cost: starCoinsStatus.cost,
          promptName: promptData.name,
          action: 'create',
          insufficient: true,
          balance: starCoinsStatus.balance,
          promptId: null,
          pendingPromptData: null
        });
      }
    } else {
      // Skip confirmation, directly create with Star Coins
      const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptCreation');
      
      if (starCoinsStatus.canUse) {
        const result = await starCoinsClient.consumeStarCoins(starCoinsStatus.cost, 'customPromptCreation');
        
        if (result.success) {
          await promptService.createPrompt(normalizedUserId, promptData);
          await loadPrompts();
          await checkPromptCreateLimit(); // Refresh limits
          setEditorOpen(false);
        } else {
          alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
        }
      } else {
        alert(`Insufficient Star Coins. You need ${starCoinsStatus.cost} Star Coins but only have ${starCoinsStatus.balance}.`);
      }
    }
  } catch (error) {
    console.error('Error saving prompt:', error);
    alert(`Error: ${error.message}`);
  }
};
  
  // Delete prompt
  const handleDeletePrompt = async () => {
    console.log('handleDeletePrompt called with:', promptToDelete);
    if (!promptToDelete || !normalizedUserId) {
      console.log('Early return - missing data:', { promptToDelete, normalizedUserId });
      return;
    }
    
    try {
      console.log('Attempting to delete prompt:', promptToDelete.id);
      await promptService.deletePrompt(normalizedUserId, promptToDelete.id);
      console.log('Prompt deleted successfully');
      loadPrompts();
      setDeleteModalOpen(false);
      setPromptToDelete(null);
      
      // Refresh create limit after deletion
      await checkPromptCreateLimit();
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };
  
  // Open delete confirmation
  const openDeleteModal = (prompt) => {
    console.log('Opening delete modal for prompt:', prompt);
    setPromptToDelete(prompt);
    setDeleteModalOpen(true);
  };
  
  // Handle using a prompt
// Handle using a prompt - FIXED VERSION
const handleUsePrompt = async (prompt) => {
  if (!normalizedUserId) {
    console.error("Cannot use prompt: No valid userId");
    return;
  }

  try {
    // First check if user has remaining free uses
    if (canUsePrompt) {
      console.log('Using prompt for free - user has remaining usage:', remainingUsage);
      
      // Increment usage counter and use the prompt for free
      const result = await incrementPromptUsage(prompt.id);
      if (result.success) {
        formatAndUsePrompt(prompt);
      } else {
        console.error('Failed to increment prompt usage:', result.error);
        alert(`Error: ${result.error}`);
      }
      return;
    }

    // User has exhausted free uses - offer Star Coins
    console.log('Free usage exhausted, offering Star Coins option');
    
    // Check if we should show confirmation dialog
    if (shouldShowConfirmation()) {
      console.log("Checking Star Coins availability for prompt usage:", prompt.name);
      
      // Check if user has enough Star Coins
      const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptUse');
      
      if (starCoinsStatus.canUse) {
        console.log("User can use Star Coins for prompt:", prompt.name);
        
        // Show confirmation modal with complete information
        setShowStarCoinsConfirm({
          visible: true,
          cost: starCoinsStatus.cost,
          promptId: prompt.id,
          promptName: prompt.name,
          action: 'use',
          pendingPromptData: null,
          insufficient: false,
          balance: 0
        });
      } else {
        // Can't use Star Coins - insufficient balance
        console.log("Insufficient Star Coins balance:", starCoinsStatus);
        setShowStarCoinsConfirm({
          visible: true,
          cost: starCoinsStatus.cost,
          promptName: prompt.name,
          action: 'use',
          insufficient: true,
          balance: starCoinsStatus.balance,
          promptId: null,
          pendingPromptData: null
        });
      }
    } else {
      console.log("Skipping confirmation, using Star Coins directly");
      
      // Skip confirmation, directly use with Star Coins
      const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptUse');
      
      if (starCoinsStatus.canUse) {
        const result = await starCoinsClient.consumeStarCoins(starCoinsStatus.cost, 'customPromptUse');
        
        if (result.success) {
          formatAndUsePrompt(prompt);
        } else {
          alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
        }
      } else {
        alert(`Insufficient Star Coins. You need ${starCoinsStatus.cost} Star Coins but only have ${starCoinsStatus.balance}.`);
      }
    }
  } catch (error) {
    console.error("Error in handleUsePrompt:", error);
    alert(`Error: ${error.message}`);
  }
};
  
  // Format prompt for use
  const formatAndUsePrompt = (prompt) => {
    let formattedPrompt;
    
    if (gameInfo) {
      // We have game context, format accordingly
      const homeTeam = gameInfo.teams?.home?.name || "Home Team";
      const awayTeam = gameInfo.teams?.away?.name || "Away Team";
      
      formattedPrompt = `${prompt.promptText} 
Game context: ${awayTeam} vs ${homeTeam}`;
    } else {
      // No specific game context
      formattedPrompt = prompt.promptText;
    }
    
    // Call the callback
    if (onUsePrompt) {
      onUsePrompt(formattedPrompt, prompt.name);
    }
  };

  // Handle Star Coins confirmation
  const handleConfirmStarCoins = async () => {
    console.log("handleConfirmStarCoins called with state:", showStarCoinsConfirm);
    
    const { promptId, promptName, cost, action, pendingPromptData } = showStarCoinsConfirm;
    
    // Keep a local copy of the state before resetting it
    const localCopy = { ...showStarCoinsConfirm };
    
    // Reset the modal state first to prevent UI issues
    setShowStarCoinsConfirm({
      visible: false,
      cost: 0,
      promptId: null,
      promptName: '',
      action: null,
      pendingPromptData: null,
      insufficient: false,
      balance: 0
    });
    
    try {
      // Determine the feature based on action
      const feature = localCopy.action === 'create' ? 'customPromptCreation' : 'customPromptUse';
      
      console.log(`Consuming ${localCopy.cost} Star Coins for ${feature}`);
      
      // Use starCoinsClient for consumption
      const result = await starCoinsClient.consumeStarCoins(localCopy.cost, feature);
      
      if (result.success) {
        console.log("Star Coins consumed successfully");
        
        if (localCopy.action === 'create' && localCopy.pendingPromptData) {
          // Create the prompt with Star Coins
          console.log("Creating prompt:", localCopy.pendingPromptData.name);
          await promptService.createPrompt(normalizedUserId, localCopy.pendingPromptData);
          await loadPrompts();
          setEditorOpen(false);
        } else if (localCopy.promptId) {
          // Use an existing prompt with Star Coins
          console.log("Using prompt with ID:", localCopy.promptId);
          const prompt = prompts.find(p => p.id === localCopy.promptId);
          if (prompt) {
            formatAndUsePrompt(prompt);
          } else {
            console.error("Prompt not found:", localCopy.promptId);
          }
        }
      } else {
        console.error(`Failed to use Star Coins for ${localCopy.action}:`, result.error);
        alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error in handleConfirmStarCoins:", error);
      alert(`Error: ${error.message}`);
    }
  };
  
  // Check if we should show the plus circle (only for first prompt creation in suggestions)
  const shouldShowPlusCircle = showInSuggestions && prompts.length === 0;

  // Format the reset time for display
  // CRITICAL UPDATE (2025-09-29): Added countdown timer display
  // Shows "Resets in Xh Ym" to clearly indicate when daily limits reset
  // Addresses user confusion about when the 7-prompt limit refreshes
  const formatResetTimer = () => {
    if (!promptResetTime) return '';

    const now = new Date();
    const resetTime = new Date(promptResetTime);
    const hoursUntilReset = Math.floor((resetTime - now) / (1000 * 60 * 60));
    const minutesUntilReset = Math.floor(((resetTime - now) % (1000 * 60 * 60)) / (1000 * 60));

    if (hoursUntilReset > 0) {
      return `Resets in ${hoursUntilReset}h ${minutesUntilReset}m`;
    } else if (minutesUntilReset > 0) {
      return `Resets in ${minutesUntilReset} minutes`;
    } else {
      return 'Resets soon';
    }
  };
  
  // Format as suggestions dropdown panel
  if (showInSuggestions) {
    return (
      <div className={`w-full ${isMobile ? 'mobile-suggestions-container' : ''}`}>
        <div className={`flex justify-between items-center mb-4 ${isMobile ? 'mobile-suggestions-header' : ''}`}>
          <div className="flex items-center gap-2">
            <BookOpen className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-emerald-400`} />
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-white`}>My Custom Prompts</h3>
            {/* CRITICAL UI UPDATE (2025-09-29): Three separate status badges */}
            {/* 1. Emerald: Prompt creation limit (how many prompts can be created) */}
            {/* 2. Violet: Daily usage limit (how many times prompts can be used today) */}
            {/* 3. Slate: Reset timer (countdown to midnight local time) */}
            <div className="flex items-center gap-2">
              <span className={`bg-emerald-500/20 text-emerald-300 ${isMobile ? 'text-xs' : 'text-xs'} px-2 py-0.5 rounded-full`}>
                {remainingPrompts} of {maxPrompts} prompts
              </span>
              <span className={`bg-violet-500/20 text-violet-300 ${isMobile ? 'text-xs' : 'text-xs'} px-2 py-0.5 rounded-full`}>
                {remainingUsage}/{maxUsage} uses today
              </span>
              {promptResetTime && (
                <span className={`bg-slate-600/30 text-slate-300 ${isMobile ? 'text-xs' : 'text-xs'} px-2 py-0.5 rounded-full`}>
                  {formatResetTimer()}
                </span>
              )}
            </div>
          </div> 
          
          <div className={`flex items-center gap-2 ${isMobile ? 'mobile-controls' : ''}`}>
            <select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              className={`bg-slate-700/50 text-white ${isMobile ? 'text-sm' : 'text-sm'} border border-slate-600/50 rounded-lg ${isMobile ? 'p-2' : 'p-1.5'}`}
            >
              {SPORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            {/* Only show plus circle if no prompts exist */}
            {shouldShowPlusCircle && (
  <button
    onClick={() => {
      setSelectedPrompt(null);
      setEditorOpen(true);
    }}
    // REMOVE: disabled={!canCreatePrompt}
    className={`${isMobile ? 'p-2.5' : 'p-2'} rounded-lg ${
      canCreatePrompt 
        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
        : 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'
    }`}
    title={canCreatePrompt ? "Create new prompt" : "Create prompt (33 Star Coins)"}
  >
    <PlusCircle className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'}`} />
  </button>
)}
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className={`animate-spin rounded-full ${isMobile ? 'h-10 w-10' : 'h-8 w-8'} border-t-2 border-b-2 border-emerald-500`}></div>
          </div>
        ) : prompts.length > 0 ? (
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            {prompts.map(prompt => (
                              <PromptChip
                key={prompt.id}
                prompt={prompt}
                onClick={handleUsePrompt}
                onEdit={(prompt) => {
                  setSelectedPrompt(prompt);
                  setEditorOpen(true);
                }}
                onDelete={openDeleteModal}
                canUse={true}
                isMobile={isMobile}
              />
            ))}
          </div>
        ) : (
          <div className={`text-center ${isMobile ? 'py-16' : 'py-24'} px-4 bg-slate-800/40 rounded-lg border border-slate-700/50 ${isMobile ? 'min-h-[300px]' : 'min-h-[400px]'}`}>
            <div className={`mx-auto ${isMobile ? 'w-16 h-16' : 'w-12 h-12'} rounded-full bg-emerald-500/10 flex items-center justify-center mb-2`}>
              <BookOpen className={`${isMobile ? 'w-8 h-8' : 'w-6 h-6'} text-emerald-400/70`} />
            </div>
            <p className={`text-white mb-2 ${isMobile ? 'text-lg' : ''}`}>You haven't created any custom prompts yet</p>
            <p className={`${isMobile ? 'text-base' : 'text-sm'} text-white/60 mb-4`}>
              Create prompts to get faster and more consistent analyses
            </p>
            <button
  onClick={() => {
    setSelectedPrompt(null);
    setEditorOpen(true);
  }}
  // REMOVE: disabled={!canCreatePrompt}
  className={`${isMobile ? 'px-6 py-3' : 'px-4 py-2'} rounded-lg ${
    canCreatePrompt 
      ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
      : 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'
  }`}
>
  <div className="flex items-center gap-2">
    <PlusCircle className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
    <span>
      {!canCreatePrompt ? `Create Prompt (33 Star Coins)` : 'Create Your First Prompt'}
    </span>
  </div>
</button>
          </div>
        )}
        
        {/* Star Coins Confirmation Modal */}
        <StarCoinsConfirmModal
  isOpen={showStarCoinsConfirm.visible}
  onClose={() => setShowStarCoinsConfirm({ 
    visible: false, 
    cost: 0, 
    promptId: null,
    promptName: '',
    action: null,
    pendingPromptData: null,
    insufficient: false,
    balance: 0
  })}
  onConfirm={showStarCoinsConfirm.insufficient ? onUpgrade : handleConfirmStarCoins}
  cost={showStarCoinsConfirm.cost}
  promptName={showStarCoinsConfirm.promptName}
  action={showStarCoinsConfirm.action}
  insufficient={showStarCoinsConfirm.insufficient}
  balance={showStarCoinsConfirm.balance}
  isMobile={isMobile}
/>
        
        {/* Other modals */}
        {renderModal(
          <PromptEditorModal
            isOpen={editorOpen}
            onClose={() => setEditorOpen(false)}
            onSave={handleSavePrompt}
            initialPrompt={selectedPrompt}
            maxPrompts={maxPrompts}
            remainingPrompts={remainingPrompts}
            canCreate={canCreatePrompt}
            isMobile={isMobile}
          />
        )}
        
        <DeletePromptModal
          isOpen={deleteModalOpen}
          onClose={() => {
            console.log('Closing delete modal');
            setDeleteModalOpen(false);
          }}
          onConfirm={handleDeletePrompt}
          promptName={promptToDelete?.name}
          isMobile={isMobile}
        />
        
        <UsageLimitModal
          isOpen={limitModalOpen}
          onClose={() => setLimitModalOpen(false)}
          onUpgrade={onUpgrade}
          limit={maxUsage}
          tier={tier}
          resetTime={promptResetTime}
          isMobile={isMobile}
        />

        {/* Mobile-specific styles */}
        <style jsx="true">{`
          .mobile-suggestions-container {
            padding: 0.5rem 0;
          }
          
          .mobile-suggestions-header {
            flex-wrap: wrap;
            gap: 0.75rem;
          }
          
          .mobile-controls {
            flex-wrap: wrap;
            justify-content: flex-end;
          }
          
          .mobile-prompt-chip {
            min-height: 120px;
          }
          
          @media (max-width: 640px) {
            .mobile-suggestions-header {
              align-items: flex-start;
            }
            
            .mobile-suggestions-header > div:first-child {
              flex: 1;
              min-width: 0;
            }
            
            .mobile-controls {
              width: 100%;
              justify-content: space-between;
              margin-top: 0.5rem;
            }
            
            .mobile-controls select {
              flex: 1;
              margin-right: 0.5rem;
            }
          }
        `}</style>
      </div>
    );
  }
  
  // Format as a standalone panel (for mobile or dedicated page) - Mobile Optimized
  return (
    <div className={`w-full max-w-4xl mx-auto ${isMobile ? 'px-2 py-4' : 'px-4 py-6'}`}>
      <div className={`flex items-center justify-between mb-6 ${isMobile ? 'mobile-standalone-header' : ''}`}>
        <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white flex items-center gap-2`}>
          <BookOpen className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-emerald-400`} />
          <span>My Custom Prompts</span>
        </h2>
        
        <button
  onClick={() => {
    setSelectedPrompt(null);
    setEditorOpen(true);
  }}
  // REMOVE: disabled={!canCreatePrompt}
  className={`${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'} rounded-lg ${
    canCreatePrompt 
      ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white'
      : 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white'
  }`}
>
  <div className="flex items-center gap-2">
    <PlusCircle className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
    <span>
      {!canCreatePrompt ? `New (33 SC)` : (isMobile ? 'New' : 'New Prompt')}
    </span>
  </div>
</button>
      </div>
      
      <div className={`bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-6 ${isMobile ? 'mobile-info-card' : ''}`}>
        <div className={`flex items-center justify-between ${isMobile ? 'flex-col space-y-3' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="bg-emerald-500/20 p-2 rounded-lg">
              <Sparkles className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'} text-emerald-400`} />
            </div>
            <div>
              <h3 className={`text-white font-medium ${isMobile ? 'text-base' : ''}`}>Custom Prompts</h3>
              <p className={`text-white/60 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                Create reusable instructions for the AI to analyze games your way
              </p>
            </div>
          </div>

          {/* CRITICAL UI UPDATE (2025-09-29): Enhanced status display in standalone panel */}
          {/* Shows three key metrics: prompt count, daily usage, and reset timer */}
          <div className={`${isMobile ? 'w-full' : ''} flex ${isMobile ? 'flex-row justify-between' : 'flex-col items-end'} gap-2`}>
            <div className={`text-center`}>
              <div className={`text-emerald-300 font-medium ${isMobile ? 'text-base' : ''}`}>
                {remainingPrompts}/{maxPrompts}
              </div>
              <div className={`text-white/60 ${isMobile ? 'text-xs' : 'text-xs'}`}>prompts</div>
            </div>
            <div className={`text-center`}>
              <div className={`text-violet-300 font-medium ${isMobile ? 'text-base' : ''}`}>
                {remainingUsage}/{maxUsage}
              </div>
              <div className={`text-white/60 ${isMobile ? 'text-xs' : 'text-xs'}`}>uses today</div>
            </div>
            {promptResetTime && (
              <div className={`text-center`}>
                <div className={`text-slate-300 font-medium ${isMobile ? 'text-base' : 'text-sm'}`}>
                  {formatResetTimer()}
                </div>
                <div className={`text-white/60 ${isMobile ? 'text-xs' : 'text-xs'}`}>until reset</div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className={`flex justify-between items-center mb-4 ${isMobile ? 'mobile-filter-row' : ''}`}>
        <div className={`text-white/70 ${isMobile ? 'text-sm' : ''}`}>
          {loading ? 'Loading...' : `${prompts.length} Custom Prompts`}
        </div>
        
        <div className="flex items-center gap-2">
          <LucideFilter className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'} text-white/60`} />
          <select
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value)}
            className={`bg-slate-700/50 text-white border border-slate-600/50 rounded-lg ${isMobile ? 'p-2 text-sm' : 'p-2'}`}
          >
            {SPORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className={`animate-spin rounded-full ${isMobile ? 'h-16 w-16' : 'h-12 w-12'} border-t-2 border-b-2 border-emerald-500`}></div>
        </div>
      ) : prompts.length > 0 ? (
        <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
          {prompts.map(prompt => (
            <div 
              key={prompt.id}
              className={`bg-slate-800/50 border border-slate-700/50 rounded-lg ${isMobile ? 'p-5' : 'p-4'} hover:border-emerald-500/30 transition-colors`}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} rounded-full flex items-center justify-center ${
                    prompt.sport !== 'all'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-slate-700/50 text-white/70'
                  }`}>
                    {prompt.sport !== 'all' ? (
                      <Check className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                    ) : (
                      <Sparkles className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                    )}
                  </div>
                  <div>
                    <h3 className={`font-medium text-white ${isMobile ? 'text-base' : ''}`}>{prompt.name}</h3>
                    {prompt.sport !== 'all' && (
                      <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-emerald-300 mt-0.5`}>
                        {SPORT_OPTIONS.find(opt => opt.value === prompt.sport)?.label}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setEditorOpen(true);
                    }}
                    className={`${isMobile ? 'p-2.5' : 'p-1.5'} rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors`}
                  >
                    <Edit className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  </button>
                  <button
                    onClick={() => openDeleteModal(prompt)}
                    className={`${isMobile ? 'p-2.5' : 'p-1.5'} rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors`}
                  >
                    <Trash className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  </button>
                </div>
              </div>
              
              {prompt.description && (
                <p className={`text-white/70 ${isMobile ? 'text-sm' : 'text-sm'} mt-2`}>
                  {prompt.description}
                </p>
              )}
              
              <div className={`${isMobile ? 'mt-4' : 'mt-3'} flex items-center justify-between`}>
                <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-white/50`}>
                  Used {prompt.usageCount || 0} times
                </div>
                
                <button
                  onClick={() => handleUsePrompt(prompt)}
                  className={`${isMobile ? 'px-4 py-2' : 'px-3 py-1'} rounded-lg ${isMobile ? 'text-sm' : 'text-sm'} bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30`}
                >
                  <div className="flex items-center gap-1.5">
                    <span>Run Prompt</span>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`text-center ${isMobile ? 'py-12' : 'py-16'} px-8 bg-slate-800/30 rounded-lg border border-slate-700/50`}>
          <div className={`mx-auto ${isMobile ? 'w-20 h-20' : 'w-16 h-16'} rounded-full bg-emerald-500/10 flex items-center justify-center mb-3`}>
            <BookOpen className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-emerald-400/70`} />
          </div>
          <h3 className={`${isMobile ? 'text-xl' : 'text-xl'} font-medium text-white mb-2`}>No Custom Prompts</h3>
          <p className={`text-white/60 max-w-md mx-auto mb-6 ${isMobile ? 'text-base' : ''}`}>
            Create custom prompts to get faster and more consistent analyses from PickStar AI
          </p>
          <button
  onClick={() => {
    setSelectedPrompt(null);
    setEditorOpen(true);
  }}
  // REMOVE: disabled={!canCreatePrompt}
  className={`${isMobile ? 'px-8 py-3' : 'px-6 py-2.5'} rounded-lg ${
    canCreatePrompt 
      ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white'
      : 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white'
  }`}
>
  <div className="flex items-center gap-2">
    <PlusCircle className={`${isMobile ? 'w-6 h-6' : 'w-5 h-5'}`} />
    <span>
      {!canCreatePrompt ? `Create Prompt (33 Star Coins)` : 'Create Your First Prompt'}
    </span>
  </div>
</button>
        </div>
      )}

      {/* Star Coins Confirmation Modal */}
      <StarCoinsConfirmModal
  isOpen={showStarCoinsConfirm.visible}
  onClose={() => setShowStarCoinsConfirm({ 
    visible: false, 
    cost: 0, 
    promptId: null,
    promptName: '',
    action: null,
    pendingPromptData: null,
    insufficient: false,
    balance: 0
  })}
  onConfirm={showStarCoinsConfirm.insufficient ? onUpgrade : handleConfirmStarCoins}
  cost={showStarCoinsConfirm.cost}
  promptName={showStarCoinsConfirm.promptName}
  action={showStarCoinsConfirm.action}
  insufficient={showStarCoinsConfirm.insufficient}
  balance={showStarCoinsConfirm.balance}
  isMobile={isMobile}
/>
      
      {/* Modals */}
      <PromptEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSavePrompt}
        initialPrompt={selectedPrompt}
        maxPrompts={maxPrompts}
        remainingPrompts={remainingPrompts}
        canCreate={canCreatePrompt}
        isMobile={isMobile}
      />
      
      <DeletePromptModal
        isOpen={deleteModalOpen}
        onClose={() => {
          console.log('Closing delete modal (standalone)');
          setDeleteModalOpen(false);
        }}
        onConfirm={handleDeletePrompt}
        promptName={promptToDelete?.name}
        isMobile={isMobile}
      />
      
      <UsageLimitModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={onUpgrade}
        limit={maxUsage}
        tier={tier}
        isMobile={isMobile}
      />

      {/* Mobile-specific styles for standalone view */}
      <style jsx="true">{`
        .mobile-standalone-header {
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .mobile-info-card .flex {
          text-align: center;
        }
        
        .mobile-filter-row {
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: flex-start;
        }
        
        @media (max-width: 640px) {
          .mobile-standalone-header {
            align-items: flex-start;
          }
          
          .mobile-standalone-header > h2 {
            flex: 1;
            min-width: 0;
          }
          
          .mobile-filter-row {
            justify-content: space-between;
          }
          
          .mobile-filter-row > div:first-child {
            flex: 1;
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomPrompts;


// // CustomPrompts.jsx - Updated version with strategic improvements
// import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
// import { db } from '../../firebase';
// import { 
//   collection, 
//   addDoc, 
//   updateDoc, 
//   deleteDoc, 
//   doc, 
//   query, 
//   where, 
//   getDocs, 
//   orderBy,
//   serverTimestamp,
//   getDoc,
//   setDoc
// } from 'firebase/firestore';
// import { 
//   PlusCircle, 
//   Edit, 
//   Trash, 
//   Save, 
//   X, 
//   BookOpen, 
//   Sparkles, 
//   Zap, 
//   Star,
//   LucideFilter, 
//   Check,
//   AlertTriangle,
//   Lock
// } from 'lucide-react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { useUsageLimiter } from '../../utils/usageLimiter';
// import { starCoinsClient } from '../../services/starCoinsClient';

// // Sport options for filtering prompts
// const SPORT_OPTIONS = [
//   { value: 'all', label: 'All Sports' },
//   { value: 'nfl', label: 'NFL' },
//   { value: 'nba', label: 'NBA' },
//   { value: 'mlb', label: 'MLB' },
//   { value: 'nhl', label: 'NHL' },
//   { value: 'epl', label: 'Premier League' },
//   { value: 'ncaab', label: 'NCAAB' },
//   { value: 'ncaaf', label: 'NCAAF' },
//   { value: 'mls', label: 'MLS' }
// ];

// // Utility functions for "don't show again" feature
// const DONT_SHOW_STORAGE_KEY = 'pickstar_prompt_confirmations_hidden';
// const DONT_SHOW_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// const shouldShowConfirmation = () => {
//   try {
//     const stored = localStorage.getItem(DONT_SHOW_STORAGE_KEY);
//     if (!stored) return true;
    
//     const { timestamp } = JSON.parse(stored);
//     const now = Date.now();
    
//     return (now - timestamp) > DONT_SHOW_DURATION;
//   } catch {
//     return true;
//   }
// };

// const hideFutureConfirmations = () => {
//   try {
//     localStorage.setItem(DONT_SHOW_STORAGE_KEY, JSON.stringify({
//       timestamp: Date.now()
//     }));
//   } catch (error) {
//     console.warn('Could not save confirmation preference:', error);
//   }
// };

// // Custom prompt service - handles data operations
// export const promptService = {
//   // Get user's prompts
//   async getUserPrompts(userId, sportFilter = 'all') {
//     try {
//       const userPromptsRef = collection(db, 'users', userId, 'prompts');
//       let q;
      
//       if (sportFilter === 'all') {
//         q = query(userPromptsRef, orderBy('createdAt', 'desc'));
//       } else {
//         q = query(userPromptsRef, where('sport', '==', sportFilter), orderBy('createdAt', 'desc'));
//       }
      
//       const querySnapshot = await getDocs(q);
//       return querySnapshot.docs.map(doc => ({
//         id: doc.id,
//         ...doc.data()
//       }));
//     } catch (error) {
//       console.error('Error fetching user prompts:', error);
//       return [];
//     }
//   },
  
//   // Create a new prompt
//   async createPrompt(userId, promptData) {
//     try {
//       // Add metadata
//       const data = {
//         ...promptData,
//         createdAt: serverTimestamp(),
//         updatedAt: serverTimestamp(),
//         usageCount: 0
//       };
      
//       const promptsRef = collection(db, 'users', userId, 'prompts');
//       const docRef = await addDoc(promptsRef, data);
//       return { id: docRef.id, ...data };
//     } catch (error) {
//       console.error('Error creating prompt:', error);
//       throw error;
//     }
//   },
  
//   // Update an existing prompt
//   async updatePrompt(userId, promptId, updates) {
//     try {
//       const promptRef = doc(db, 'users', userId, 'prompts', promptId);
//       await updateDoc(promptRef, {
//         ...updates,
//         updatedAt: serverTimestamp()
//       });
//       return true;
//     } catch (error) {
//       console.error('Error updating prompt:', error);
//       throw error;
//     }
//   },
  
//   // Delete a prompt
//   async deletePrompt(userId, promptId) {
//     try {
//       const promptRef = doc(db, 'users', userId, 'prompts', promptId);
//       await deleteDoc(promptRef);
//       return true;
//     } catch (error) {
//       console.error('Error deleting prompt:', error);
//       throw error;
//     }
//   }
// };

// // Prompt Chip Component
// // Enhanced PromptChip with more elegant design
// export const PromptChip = ({ prompt, onClick, onEdit, onDelete, canUse = true }) => {
//   // Get the sport label if applicable
//   const sportLabel = prompt.sport !== 'all' 
//     ? SPORT_OPTIONS.find(opt => opt.value === prompt.sport)?.label 
//     : null;
    
//   return (
//     <div className="group relative bg-slate-800/40 hover:bg-slate-800/70 border border-slate-700/50 hover:border-emerald-500/30 rounded-lg p-3 transition-all duration-200">
//       <div className="flex items-start">
//         <div className={`h-7 w-7 rounded-md flex items-center justify-center mr-2 ${
//           sportLabel 
//             ? 'bg-emerald-500/20 text-emerald-400' 
//             : 'bg-slate-700/50 text-slate-300'
//         }`}>
//           <Sparkles className="w-3.5 h-3.5" />
//         </div>
        
//         <div className="flex-1 min-w-0">
//           <h4 className="text-sm font-medium text-white truncate">{prompt.name}</h4>
//           {sportLabel && (
//             <span className="text-xs text-emerald-400/80 block mt-0.5">
//               {sportLabel}
//             </span>
//           )}
//           {prompt.description && (
//             <p className="text-xs text-slate-400 mt-1 line-clamp-1">
//               {prompt.description}
//             </p>
//           )}
//         </div>
        
//         <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
//           <button
//             onClick={() => onEdit(prompt)}
//             className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
//             title="Edit prompt"
//           >
//             <Edit className="w-3 h-3" />
//           </button>
//           <button
//             onClick={() => onDelete(prompt.id)}
//             className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
//             title="Delete prompt"
//           >
//             <Trash className="w-3 h-3" />
//           </button>
//         </div>
//       </div>
      
//       <button
//         onClick={() => canUse && onClick(prompt)}
//         disabled={!canUse}
//         className={`w-full mt-3 px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 rounded-md transition-colors ${
//           canUse
//             ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400"
//             : "bg-slate-700/30 text-slate-500 cursor-not-allowed"
//         }`}
//       >
//         {canUse ? <Sparkles className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
//         <span>Use Prompt</span>
//       </button>
//     </div>
//   );
// };

// // Prompt Editor Modal Component
// export const PromptEditorModal = ({ 
//   isOpen, 
//   onClose, 
//   onSave, 
//   initialPrompt = null,
//   maxPrompts,
//   remainingPrompts,
//   canCreate 
// }) => {
//   const [name, setName] = useState(initialPrompt?.name || '');
//   const [description, setDescription] = useState(initialPrompt?.description || '');
//   const [promptText, setPromptText] = useState(initialPrompt?.promptText || '');
//   const [sport, setSport] = useState(initialPrompt?.sport || 'all');
//   const [errors, setErrors] = useState({});
  
//   // Reset form when opened
//   useEffect(() => {
//     if (isOpen) {
//       setName(initialPrompt?.name || '');
//       setDescription(initialPrompt?.description || '');
//       setPromptText(initialPrompt?.promptText || '');
//       setSport(initialPrompt?.sport || 'all');
//       setErrors({});
//     }
//   }, [isOpen, initialPrompt]);
  
//   // Validate form
//   const validate = () => {
//     const newErrors = {};
    
//     if (!name.trim()) newErrors.name = 'Name is required';
//     if (!promptText.trim()) newErrors.promptText = 'Prompt text is required';
    
//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };
  
//   // Save prompt
//   const handleSave = () => {
//     if (!validate()) return;
    
//     const promptData = {
//       name,
//       description,
//       promptText,
//       sport
//     };
    
//     onSave(promptData, initialPrompt?.id);
//   };
  
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-8"
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ y: 20, opacity: 0, scale: 0.95 }}
//             animate={{ y: 0, opacity: 1, scale: 1 }}
//             exit={{ y: 20, opacity: 0, scale: 0.95 }}
//             transition={{ type: 'spring', damping: 25, stiffness: 300 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto my-auto"
//             onClick={e => e.stopPropagation()}
//           >
//             <div className="flex items-center justify-between mb-4">
//               <div className="flex items-center gap-2">
//                 <div className="w-8 h-8 bg-gradient-to-br from-emerald-500/20 to-violet-500/20 rounded-lg flex items-center justify-center">
//                   {initialPrompt ? (
//                     <Edit className="w-4 h-4 text-emerald-400" />
//                   ) : (
//                     <PlusCircle className="w-4 h-4 text-emerald-400" />
//                   )}
//                 </div>
//                 <h3 className="text-lg font-medium text-white">
//                   {initialPrompt ? 'Edit Prompt' : 'Create New Prompt'}
//                 </h3>
//               </div>
//               <button
//                 onClick={onClose}
//                 className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>
            
//             {/* Warning if at limit */}
//             {!initialPrompt && !canCreate && (
//               <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
//                 <div className="flex gap-2">
//                   <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
//                   <div>
//                     <p className="text-amber-300 font-medium">Prompt limit reached</p>
//                     <p className="text-amber-200/70 text-sm mt-0.5">
//                       You can create up to {maxPrompts} custom prompts on your current plan.
//                       Delete an existing prompt to create a new one.
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             {/* Form */}
//             <div className="space-y-4">
//               {/* Name field */}
//               <div>
//                 <label htmlFor="prompt-name" className="block text-white text-sm font-medium mb-1">
//                   Prompt Name
//                 </label>
//                 <input
//                   id="prompt-name"
//                   type="text"
//                   value={name}
//                   onChange={e => setName(e.target.value)}
//                   placeholder="E.g., Underdog Analysis, Value Bet Finder"
//                   className={`w-full bg-slate-900/50 border ${
//                     errors.name ? 'border-red-500/50' : 'border-slate-700/50'
//                   } rounded-lg p-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50`}
//                 />
//                 {errors.name && (
//                   <p className="mt-1 text-xs text-red-400">{errors.name}</p>
//                 )}
//               </div>
              
//               {/* Description field */}
//               <div>
//                 <label htmlFor="prompt-desc" className="block text-white text-sm font-medium mb-1">
//                   Description (Optional)
//                 </label>
//                 <input
//                   id="prompt-desc"
//                   type="text"
//                   value={description}
//                   onChange={e => setDescription(e.target.value)}
//                   placeholder="What this prompt is useful for"
//                   className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg p-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
//                 />
//               </div>
              
//               {/* Sport selection */}
//               <div>
//                 <label htmlFor="prompt-sport" className="block text-white text-sm font-medium mb-1">
//                   Sport (Optional)
//                 </label>
//                 <select
//                   id="prompt-sport"
//                   value={sport}
//                   onChange={e => setSport(e.target.value)}
//                   className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg p-2.5 text-white focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
//                 >
//                   {SPORT_OPTIONS.map(option => (
//                     <option key={option.value} value={option.value}>
//                       {option.label}
//                     </option>
//                   ))}
//                 </select>
//                 <p className="mt-1 text-xs text-slate-400">
//                   Associate this prompt with a specific sport for easier filtering
//                 </p>
//               </div>
              
//               {/* Prompt text */}
//               <div>
//                 <label htmlFor="prompt-text" className="block text-white text-sm font-medium mb-1">
//                   Prompt Instructions
//                 </label>
//                 <textarea
//                   id="prompt-text"
//                   value={promptText}
//                   onChange={e => setPromptText(e.target.value)}
//                   placeholder="Enter your custom instructions for the AI..."
//                   rows={6}
//                   className={`w-full bg-slate-900/50 border ${
//                     errors.promptText ? 'border-red-500/50' : 'border-slate-700/50'
//                   } rounded-lg p-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 resize-none`}
//                 ></textarea>
//                 {errors.promptText ? (
//                   <p className="mt-1 text-xs text-red-400">{errors.promptText}</p>
//                 ) : (
//                   <p className="mt-1 text-xs text-slate-400">
//                     Write detailed instructions for how the AI should analyze games or provide insights
//                   </p>
//                 )}
//               </div>
//             </div>
            
//             {/* Actions */}
//             <div className="flex justify-end gap-3 mt-6">
//               <button
//                 onClick={onClose}
//                 className="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSave}
//                 disabled={!initialPrompt && !canCreate}
//                 className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
//                   !initialPrompt && !canCreate
//                     ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
//                     : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white'
//                 }`}
//               >
//                 <Save className="w-4 h-4" />
//                 <span>Save Prompt</span>
//               </button>
//             </div>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// // Delete confirmation modal
// export const DeletePromptModal = ({ isOpen, onClose, onConfirm, promptName }) => {
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ y: 20, opacity: 0, scale: 0.95 }}
//             animate={{ y: 0, opacity: 1, scale: 1 }}
//             exit={{ y: 20, opacity: 0, scale: 0.95 }}
//             transition={{ type: 'spring', damping: 25, stiffness: 300 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-md"
//             onClick={e => e.stopPropagation()}
//           >
//             <div className="text-center mb-4">
//               <div className="mx-auto w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mb-3">
//                 <Trash className="w-6 h-6 text-red-400" />
//               </div>
//               <h3 className="text-xl font-medium text-white mb-2">Delete Prompt</h3>
//               <p className="text-white/70">
//                 Are you sure you want to delete <strong className="text-red-300">{promptName}</strong>?
//                 This action cannot be undone.
//               </p>
//             </div>
            
//             <div className="flex gap-3 mt-6">
//               <button
//                 onClick={onClose}
//                 className="flex-1 px-4 py-2.5 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={onConfirm}
//                 className="flex-1 px-4 py-2.5 bg-red-500/90 hover:bg-red-500 text-white rounded-lg transition-colors"
//               >
//                 Delete
//               </button>
//             </div>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// export const UsageLimitModal = ({ isOpen, onClose, onUpgrade, limit, tier }) => {
//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
//           onClick={onClose}
//         >
//           <motion.div
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-md"
//             onClick={e => e.stopPropagation()}
//           >
//             <div className="text-center mb-4">
//               <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
//                 <AlertTriangle className="w-6 h-6 text-amber-400" />
//               </div>
//               <h3 className="text-xl font-medium text-white mb-2">Usage Limit Reached</h3>
//               <p className="text-white/70">
//                 {tier === 'basic'
//                   ? `You've reached your daily limit of ${limit} custom prompt use.`
//                   : `You've reached your daily limit of ${limit} custom prompt uses on your Pro plan.`}
//               </p>
//             </div>
            
//             <div className="bg-slate-900/50 rounded-lg p-4 my-4 border border-slate-700/50">
//               <div className="flex items-center justify-between mb-2">
//                 <div className="flex items-center gap-2">
//                   <div className="p-1.5 bg-emerald-500/20 rounded-md">
//                     <Zap className="w-4 h-4 text-emerald-400" />
//                   </div>
//                   <span className="text-white font-medium">Basic Plan</span>
//                 </div>
//                 <span className="text-white/60">1 use/day</span>
//               </div>
              
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-2">
//                   <div className="p-1.5 bg-violet-500/20 rounded-md">
//                     <Star className="w-4 h-4 text-violet-400" />
//                   </div>
//                   <span className="text-white font-medium">Pro Plan</span>
//                 </div>
//                 <span className="text-white/60">7 uses/day</span>
//               </div>
//             </div>
            
//             <div className="mt-3 text-center">
//               <p className="text-white/70 text-sm">
//                 <Star className="inline-block w-3.5 h-3.5 mr-1 text-amber-400" />
//                 You can also use 11 Star Coins per prompt when you've reached your daily limit
//               </p>
//             </div>
            
//             <div className="flex gap-3 mt-6">
//               <button
//                 onClick={onClose}
//                 className="flex-1 px-4 py-2.5 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
//               >
//                 Close
//               </button>
//               {tier === 'basic' && (
//                 <button
//                   onClick={onUpgrade}
//                   className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-lg transition-colors"
//                 >
//                   Upgrade to Pro
//                 </button>
//               )}
//             </div>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// // Updated Star Coins confirmation modal
// export const StarCoinsConfirmModal = ({ 
//   isOpen, 
//   onClose, 
//   onConfirm, 
//   cost, 
//   promptName, 
//   action,
//   showDontShowAgain = true 
// }) => {
//   const [dontShowAgain, setDontShowAgain] = useState(false);

//   const handleConfirm = () => {
//     if (dontShowAgain) {
//       hideFutureConfirmations();
//     }
//     onConfirm();
//   };

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
//           onClick={onClose}
//         >
//           <motion.div
//             initial={{ y: 20, opacity: 0, scale: 0.95 }}
//             animate={{ y: 0, opacity: 1, scale: 1 }}
//             exit={{ y: 20, opacity: 0, scale: 0.95 }}
//             className="bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl p-5 w-full max-w-sm"
//             onClick={e => e.stopPropagation()}
//           >
//             <div className="flex items-center gap-3 mb-4">
//               <Star className="w-6 h-6 text-amber-400" />
//               <h3 className="text-lg font-medium text-white">
//                 {action === 'create' ? 'Create' : 'Run'} {promptName}?
//               </h3>
//             </div>
            
//             <p className="text-white/80 mb-4">
//               This will use {cost} Star Coins.
//             </p>

//             {showDontShowAgain && (
//               <div className="flex items-center gap-2 mb-4 p-3 bg-slate-900/50 rounded-lg">
//                 <input
//                   type="checkbox"
//                   id="dont-show-again"
//                   checked={dontShowAgain}
//                   onChange={(e) => setDontShowAgain(e.target.checked)}
//                   className="w-4 h-4 text-amber-500 bg-slate-700 border-slate-600 rounded focus:ring-amber-500 focus:ring-2"
//                 />
//                 <label htmlFor="dont-show-again" className="text-sm text-white/70">
//                   Don't show confirmations for 24 hours
//                 </label>
//               </div>
//             )}
            
//             <div className="flex gap-3">
//               <button
//                 onClick={onClose}
//                 className="flex-1 py-2 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleConfirm}
//                 className="flex-1 py-2 bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white rounded-lg transition-colors"
//               >
//                 Confirm
//               </button>
//             </div>
//           </motion.div>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// };

// // Main component for Custom Prompts feature
// const CustomPrompts = ({ 
//   userId,
//   gameInfo = null,
//   onUsePrompt,
//   onUpgrade,
//   isMobile = false,
//   showInSuggestions = true
// }) => {
//   // Normalize userId - ensuring it's properly formatted for Firebase
//   const normalizedUserId = useMemo(() => {
//     if (typeof userId === 'string' && userId) {
//       return userId;
//     } else if (userId && typeof userId === 'object') {
//       return userId.uid || userId.id || null;
//     }
//     return null;
//   }, [userId]);
  
//   // State
//   const [prompts, setPrompts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedPrompt, setSelectedPrompt] = useState(null);
//   const [editorOpen, setEditorOpen] = useState(false);
//   const [deleteModalOpen, setDeleteModalOpen] = useState(false);
//   const [promptToDelete, setPromptToDelete] = useState(null);
//   const [sportFilter, setSportFilter] = useState('all');
//   const [limitModalOpen, setLimitModalOpen] = useState(false);
  
//   // Use the existing hook from useUsageLimiter instead of creating a new one
//   const usageLimiter = useUsageLimiter(normalizedUserId);

//   const [showStarCoinsConfirm, setShowStarCoinsConfirm] = useState({
//     visible: false,
//     cost: 0,
//     promptId: null,
//     promptName: '',
//     action: null,
//     pendingPromptData: null
//   });
  
//   // Extract relevant prompt limit data from usageLimiter
//   const { 
//     canCreatePrompt, 
//     remainingPrompts, 
//     maxPrompts, 
//     canUsePrompt,
//     starCoins,
//     remainingUsage,
//     maxUsage,
//     tier,
//     checkPromptCreateLimit,
//     checkPromptUsageLimit,
//     incrementPromptUsage
//   } = usageLimiter;
  
//   // Load prompts
//   const loadPrompts = useCallback(async () => {
//     if (!normalizedUserId) {
//       console.warn('Cannot load prompts: No valid userId');
//       setLoading(false);
//       return;
//     }
    
//     setLoading(true);
//     try {
//       const userPrompts = await promptService.getUserPrompts(normalizedUserId, sportFilter);
//       setPrompts(userPrompts);
//     } catch (error) {
//       console.error('Error loading prompts:', error);
//     } finally {
//       setLoading(false);
//     }
//   }, [normalizedUserId, sportFilter]);
  
//   // Load prompts on mount and when filter changes
//   useEffect(() => {
//     loadPrompts();
//   }, [loadPrompts]);
  
//   // Initialize once on component mount
//   const hasInitializedRef = useRef(false);
//   useEffect(() => {
//     if (!hasInitializedRef.current && normalizedUserId) {
//       hasInitializedRef.current = true;
      
//       // Initial check of limits
//       checkPromptCreateLimit();
//       checkPromptUsageLimit();
//     }
//   }, [normalizedUserId, checkPromptCreateLimit, checkPromptUsageLimit]);
  
//   // Create or update prompt
//   const handleSavePrompt = async (promptData, promptId = null) => {
//     if (!normalizedUserId) return;
    
//     try {
//       if (promptId) {
//         // Update existing - no coins needed
//         await promptService.updatePrompt(normalizedUserId, promptId, promptData);
//         loadPrompts();
//         setEditorOpen(false);
//         return;
//       }
      
//       // For NEW prompts - check if we should show confirmation
//       if (shouldShowConfirmation()) {
//         // Check if user has enough Star Coins
//         const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptCreation');
        
//         if (starCoinsStatus.canUse) {
//           // Show Star Coins confirmation for creation
//           setShowStarCoinsConfirm({
//             visible: true,
//             cost: starCoinsStatus.cost,
//             promptId: null,
//             promptName: promptData.name,
//             action: 'create',
//             pendingPromptData: promptData
//           });
//         } else {
//           // Can't use Star Coins - show insufficient coins message
//           alert(`You need ${starCoinsStatus.cost} Star Coins to create a prompt. Current balance: ${starCoinsStatus.balance}`);
//         }
//       } else {
//         // Skip confirmation, directly create with Star Coins
//         const result = await starCoinsClient.consumeStarCoins(11, 'customPromptCreation'); // Assuming 11 is the cost
        
//         if (result.success) {
//           await promptService.createPrompt(normalizedUserId, promptData);
//           await loadPrompts();
//           setEditorOpen(false);
//         } else {
//           alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
//         }
//       }
//     } catch (error) {
//       console.error('Error saving prompt:', error);
//       alert(`Error: ${error.message}`);
//     }
//   };
  
//   // Delete prompt
//   const handleDeletePrompt = async () => {
//     if (!promptToDelete || !normalizedUserId) return;
    
//     try {
//       await promptService.deletePrompt(normalizedUserId, promptToDelete.id);
//       loadPrompts();
//       setDeleteModalOpen(false);
//       setPromptToDelete(null);
      
//       // Refresh create limit after deletion
//       await checkPromptCreateLimit();
//     } catch (error) {
//       console.error('Error deleting prompt:', error);
//     }
//   };
  
//   // Open delete confirmation
//   const openDeleteModal = (prompt) => {
//     setPromptToDelete(prompt);
//     setDeleteModalOpen(true);
//   };
  
//   // Handle using a prompt
//   const handleUsePrompt = async (prompt) => {
//     try {
//       if (shouldShowConfirmation()) {
//         // Check if user has enough Star Coins
//         const starCoinsStatus = await starCoinsClient.canUseStarCoinsForFeature(normalizedUserId, 'customPromptUse');
        
//         if (starCoinsStatus.canUse) {
//           // Show confirmation modal
//           setShowStarCoinsConfirm({
//             visible: true,
//             cost: starCoinsStatus.cost,
//             promptId: prompt.id,
//             promptName: prompt.name,
//             action: 'use'
//           });
//         } else {
//           // Can't use Star Coins
//           alert(`You need ${starCoinsStatus.cost} Star Coins to use this prompt. Current balance: ${starCoinsStatus.balance}`);
//         }
//       } else {
//         // Skip confirmation, directly use with Star Coins
//         const result = await starCoinsClient.consumeStarCoins(11, 'customPromptUse'); // Assuming 11 is the cost
        
//         if (result.success) {
//           formatAndUsePrompt(prompt);
//         } else {
//           alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
//         }
//       }
//     } catch (error) {
//       console.error("Error checking Star Coins:", error);
//       alert(`Error: ${error.message}`);
//     }
//   };
  
//   // Format prompt for use
//   const formatAndUsePrompt = (prompt) => {
//     let formattedPrompt;
    
//     if (gameInfo) {
//       // We have game context, format accordingly
//       const homeTeam = gameInfo.teams?.home?.name || "Home Team";
//       const awayTeam = gameInfo.teams?.away?.name || "Away Team";
      
//       formattedPrompt = `${prompt.promptText} 
// Game context: ${awayTeam} vs ${homeTeam}`;
//     } else {
//       // No specific game context
//       formattedPrompt = prompt.promptText;
//     }
    
//     // Call the callback
//     if (onUsePrompt) {
//       onUsePrompt(formattedPrompt, prompt.name);
//     }
//   };

//   // Handle Star Coins confirmation
//   const handleConfirmStarCoins = async () => {
//     const { promptId, promptName, cost, action, pendingPromptData } = showStarCoinsConfirm;
    
//     // Determine the feature based on action
//     const feature = action === 'create' ? 'customPromptCreation' : 'customPromptUse';
    
//     // Use starCoinsClient for consumption
//     const result = await starCoinsClient.consumeStarCoins(cost, feature);
    
//     if (result.success) {
//       if (action === 'create' && pendingPromptData) {
//         // Create the prompt with Star Coins
//         await promptService.createPrompt(normalizedUserId, pendingPromptData);
//         await loadPrompts();
//         setEditorOpen(false);
//       } else if (promptId) {
//         // Use an existing prompt with Star Coins
//         const prompt = prompts.find(p => p.id === promptId);
//         if (prompt) {
//           formatAndUsePrompt(prompt);
//         }
//       }
      
//       // Close the confirmation modal
//       setShowStarCoinsConfirm({
//         visible: false,
//         cost: 0,
//         promptId: null,
//         promptName: '',
//         action: null,
//         pendingPromptData: null
//       });
//     } else {
//       console.error(`Failed to use Star Coins for ${action}:`, result.error);
//       alert(`Could not use Star Coins: ${result.error || 'Unknown error'}`);
//     }
//   };
  
//   // Check if we should show the plus circle (only for first prompt creation in suggestions)
//   const shouldShowPlusCircle = showInSuggestions && prompts.length === 0;
  
//   // Format as suggestions dropdown panel
//   if (showInSuggestions) {
//     return (
//       <div className="w-full">
//         <div className="flex justify-between items-center mb-4">
//           <div className="flex items-center gap-2">
//             <BookOpen className="w-5 h-5 text-emerald-400" />
//             <h3 className="text-lg font-medium text-white">My Custom Prompts</h3>
//             <span className="bg-emerald-500/20 text-emerald-300 text-xs px-2 py-0.5 rounded-full">
//               {remainingPrompts} of {maxPrompts} remaining
//             </span>
//           </div> 
          
//           <div className="flex items-center gap-2">
//             <select
//               value={sportFilter}
//               onChange={(e) => setSportFilter(e.target.value)}
//               className="bg-slate-700/50 text-white text-sm border border-slate-600/50 rounded-lg p-1.5"
//             >
//               {SPORT_OPTIONS.map(option => (
//                 <option key={option.value} value={option.value}>
//                   {option.label}
//                 </option>
//               ))}
//             </select>
            
//             {/* Only show plus circle if no prompts exist */}
//             {shouldShowPlusCircle && (
//               <button
//                 onClick={() => {
//                   setSelectedPrompt(null);
//                   setEditorOpen(true);
//                 }}
//                 disabled={!canCreatePrompt}
//                 className={`p-2 rounded-lg ${
//                   canCreatePrompt
//                     ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
//                     : "bg-slate-700/50 text-slate-400 cursor-not-allowed"
//                 }`}
//                 title={
//                   canCreatePrompt
//                     ? "Create new prompt"
//                     : `Limit reached (${maxPrompts} max)`
//                 }
//               >
//                 <PlusCircle className="w-5 h-5" />
//               </button>
//             )}
//           </div>
//         </div>
        
//         {loading ? (
//           <div className="flex justify-center items-center py-8">
//             <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
//           </div>
//         ) : prompts.length > 0 ? (
//           <div className="grid grid-cols-2 gap-4">
//             {prompts.map(prompt => (
//               <div 
//                 key={prompt.id} 
//                 className="group relative bg-slate-800/40 hover:bg-slate-800/70 border border-slate-700/50 hover:border-emerald-500/30 rounded-lg p-3 transition-all duration-200"
//               >
//                 <div className="flex items-start mb-2">
//                   <div className={`h-7 w-7 rounded-md flex items-center justify-center mr-2 ${
//                     prompt.sport !== 'all' 
//                       ? 'bg-emerald-500/20 text-emerald-400' 
//                       : 'bg-slate-700/50 text-slate-300'
//                   }`}>
//                     <Sparkles className="w-3.5 h-3.5" />
//                   </div>
//                   <div className="flex-1 min-w-0">
//                     <h4 className="text-sm font-medium text-white truncate">{prompt.name}</h4>
//                     {prompt.sport !== 'all' && (
//                       <span className="text-xs text-emerald-400/80 block mt-0.5">
//                         {SPORT_OPTIONS.find(opt => opt.value === prompt.sport)?.label}
//                       </span>
//                     )}
//                   </div>
//                   <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
//                     <button
//                       onClick={() => {
//                         setSelectedPrompt(prompt);
//                         setEditorOpen(true);
//                       }}
//                       className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
//                     >
//                       <Edit className="w-3 h-3" />
//                     </button>
//                     <button
//                       onClick={() => openDeleteModal(prompt)}
//                       className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
//                     >
//                       <Trash className="w-3 h-3" />
//                     </button>
//                   </div>
//                 </div>
                
//                 {prompt.description && (
//                   <p className="text-xs text-slate-400 mb-2 line-clamp-1">
//                     {prompt.description}
//                   </p>
//                 )}
                
//                 <button
//                   onClick={() => handleUsePrompt(prompt)}
//                   className="w-full mt-1 px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors"
//                 >
//                   <Sparkles className="w-3 h-3" />
//                   <span>Use Prompt</span>
//                 </button>
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="text-center py-24 px-4 bg-slate-800/40 rounded-lg border border-slate-700/50 min-h-[400px]">
//             <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
//               <BookOpen className="w-6 h-6 text-emerald-400/70" />
//             </div>
//             <p className="text-white mb-2">You haven't created any custom prompts yet</p>
//             <p className="text-sm text-white/60 mb-4">
//               Create prompts to get faster and more consistent analyses
//             </p>
//             <button
//               onClick={() => {
//                 setSelectedPrompt(null);
//                 setEditorOpen(true);
//               }}
//               disabled={!canCreatePrompt}
//               className={`px-4 py-2 rounded-lg ${
//                 canCreatePrompt
//                   ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
//                   : "bg-slate-700/50 text-slate-400 cursor-not-allowed"
//               }`}
//             >
//               <div className="flex items-center gap-2">
//                 <PlusCircle className="w-4 h-4" />
//                 <span>Create Your First Prompt</span>
//               </div>
//             </button>
//           </div>
//         )}
        
//         {/* Star Coins Confirmation Modal */}
//         <StarCoinsConfirmModal
//           isOpen={showStarCoinsConfirm.visible}
//           onClose={() => setShowStarCoinsConfirm({ 
//             visible: false, 
//             cost: 0, 
//             promptId: null,
//             promptName: '',
//             action: null,
//             pendingPromptData: null
//           })}
//           onConfirm={handleConfirmStarCoins}
//           cost={showStarCoinsConfirm.cost}
//           promptName={showStarCoinsConfirm.promptName}
//           action={showStarCoinsConfirm.action}
//         />
        
//         {/* Other modals */}
//         <PromptEditorModal
//           isOpen={editorOpen}
//           onClose={() => setEditorOpen(false)}
//           onSave={handleSavePrompt}
//           initialPrompt={selectedPrompt}
//           maxPrompts={maxPrompts}
//           remainingPrompts={remainingPrompts}
//           canCreate={canCreatePrompt}
//         />
        
//         <DeletePromptModal
//           isOpen={deleteModalOpen}
//           onClose={() => setDeleteModalOpen(false)}
//           onConfirm={handleDeletePrompt}
//           promptName={promptToDelete?.name}
//         />
        
//         <UsageLimitModal
//           isOpen={limitModalOpen}
//           onClose={() => setLimitModalOpen(false)}
//           onUpgrade={onUpgrade}
//           limit={maxUsage}
//           tier={tier}
//         />
//       </div>
//     );
//   }
  
//   // Format as a standalone panel (for mobile or dedicated page)
//   return (
//     <div className="w-full max-w-4xl mx-auto px-4 py-6">
//       <div className="flex items-center justify-between mb-6">
//         <h2 className="text-2xl font-bold text-white flex items-center gap-2">
//           <BookOpen className="w-6 h-6 text-emerald-400" />
//           <span>My Custom Prompts</span>
//         </h2>
        
//         <button
//           onClick={() => {
//             setSelectedPrompt(null);
//             setEditorOpen(true);
//           }}
//           disabled={!canCreatePrompt}
//           className={`px-4 py-2 rounded-lg ${
//             canCreatePrompt
//               ? "bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white"
//               : "bg-slate-700/50 text-slate-400 cursor-not-allowed"
//           }`}
//         >
//           <div className="flex items-center gap-2">
//             <PlusCircle className="w-4 h-4" />
//             <span>New Prompt</span>
//           </div>
//         </button>
//       </div>
      
//       <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 mb-6">
//         <div className="flex items-center justify-between">
//           <div className="flex items-center gap-3">
//             <div className="bg-emerald-500/20 p-2 rounded-lg">
//               <Sparkles className="w-5 h-5 text-emerald-400" />
//             </div>
//             <div>
//               <h3 className="text-white font-medium">Custom Prompts</h3>
//               <p className="text-white/60 text-sm">
//                 Create reusable instructions for the AI to analyze games your way
//               </p>
//             </div>
//           </div>
          
//           <div className="text-right">
//             <div className="text-emerald-300 font-medium">
//               {remainingPrompts} of {maxPrompts}
//             </div>
//             <div className="text-white/60 text-sm">prompts remaining</div>
//           </div>
//         </div>
//       </div>
      
//       <div className="flex justify-between items-center mb-4">
//         <div className="text-white/70">
//           {loading ? 'Loading...' : `${prompts.length} Custom Prompts`}
//         </div>
        
//         <div className="flex items-center gap-2">
//           <LucideFilter className="w-4 h-4 text-white/60" />
//           <select
//             value={sportFilter}
//             onChange={(e) => setSportFilter(e.target.value)}
//             className="bg-slate-700/50 text-white border border-slate-600/50 rounded-lg p-2"
//           >
//             {SPORT_OPTIONS.map(option => (
//               <option key={option.value} value={option.value}>
//                 {option.label}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>
      
//       {loading ? (
//         <div className="flex justify-center items-center py-16">
//           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
//         </div>
//       ) : prompts.length > 0 ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//           {prompts.map(prompt => (
//             <div 
//               key={prompt.id}
//               className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 hover:border-emerald-500/30 transition-colors"
//             >
//               <div className="flex justify-between items-start">
//                 <div className="flex items-center gap-2">
//                   <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
//                     prompt.sport !== 'all'
//                       ? 'bg-emerald-500/20 text-emerald-400'
//                       : 'bg-slate-700/50 text-white/70'
//                   }`}>
//                     {prompt.sport !== 'all' ? (
//                       <Check className="w-4 h-4" />
//                     ) : (
//                       <Sparkles className="w-4 h-4" />
//                     )}
//                   </div>
//                   <div>
//                     <h3 className="font-medium text-white">{prompt.name}</h3>
//                     {prompt.sport !== 'all' && (
//                       <div className="text-xs text-emerald-300 mt-0.5">
//                         {SPORT_OPTIONS.find(opt => opt.value === prompt.sport)?.label}
//                       </div>
//                     )}
//                   </div>
//                 </div>
                
//                 <div className="flex gap-1">
//                   <button
//                     onClick={() => {
//                       setSelectedPrompt(prompt);
//                       setEditorOpen(true);
//                     }}
//                     className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
//                   >
//                     <Edit className="w-4 h-4" />
//                   </button>
//                   <button
//                     onClick={() => openDeleteModal(prompt)}
//                     className="p-1.5 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors"
//                   >
//                     <Trash className="w-4 h-4" />
//                   </button>
//                 </div>
//               </div>
              
//               {prompt.description && (
//                 <p className="text-white/70 text-sm mt-2">
//                   {prompt.description}
//                 </p>
//               )}
              
//               <div className="mt-3 flex items-center justify-between">
//                 <div className="text-xs text-white/50">
//                   Used {prompt.usageCount || 0} times
//                 </div>
                
//                 <button
//                   onClick={() => handleUsePrompt(prompt)}
//                   className="px-3 py-1 rounded-lg text-sm bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
//                 >
//                   <div className="flex items-center gap-1.5">
//                     <span>Use Prompt</span>
//                   </div>
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       ) : (
//         <div className="text-center py-16 px-8 bg-slate-800/30 rounded-lg border border-slate-700/50">
//           <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
//             <BookOpen className="w-8 h-8 text-emerald-400/70" />
//           </div>
//           <h3 className="text-xl font-medium text-white mb-2">No Custom Prompts</h3>
//           <p className="text-white/60 max-w-md mx-auto mb-6">
//             Create custom prompts to get faster and more consistent analyses from PickStar AI
//           </p>
//           <button
//             onClick={() => {
//               setSelectedPrompt(null);
//               setEditorOpen(true);
//             }}
//             disabled={!canCreatePrompt}
//             className={`px-6 py-2.5 rounded-lg ${
//               canCreatePrompt
//                 ? "bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white"
//                 : "bg-slate-700/50 text-slate-400 cursor-not-allowed"
//             }`}
//           >
//             <div className="flex items-center gap-2">
//               <PlusCircle className="w-5 h-5" />
//               <span>Create Your First Prompt</span>
//             </div>
//           </button>
//         </div>
//       )}

//       {/* Star Coins Confirmation Modal */}
//       <StarCoinsConfirmModal
//         isOpen={showStarCoinsConfirm.visible}
//         onClose={() => setShowStarCoinsConfirm({ 
//           visible: false, 
//           cost: 0, 
//           promptId: null,
//           promptName: '',
//           action: null,
//           pendingPromptData: null
//         })}
//         onConfirm={handleConfirmStarCoins}
//         cost={showStarCoinsConfirm.cost}
//         promptName={showStarCoinsConfirm.promptName}
//         action={showStarCoinsConfirm.action}
//       />
      
//       {/* Modals */}
//       <PromptEditorModal
//         isOpen={editorOpen}
//         onClose={() => setEditorOpen(false)}
//         onSave={handleSavePrompt}
//         initialPrompt={selectedPrompt}
//         maxPrompts={maxPrompts}
//         remainingPrompts={remainingPrompts}
//         canCreate={canCreatePrompt}
//       />
      
//       <DeletePromptModal
//         isOpen={deleteModalOpen}
//         onClose={() => setDeleteModalOpen(false)}
//         onConfirm={handleDeletePrompt}
//         promptName={promptToDelete?.name}
//       />
      
//       <UsageLimitModal
//         isOpen={limitModalOpen}
//         onClose={() => setLimitModalOpen(false)}
//         onUpgrade={onUpgrade}
//         limit={maxUsage}
//         tier={tier}
//       />
//     </div>
//   );
// };

// export default CustomPrompts;